import { AlertTriangle } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { BOLT, fmtCredits, fmtInt, fmtMoney } from '@/lib/format'

/** GET /accounting/summary → `advances` (core.accounting.advances.balances). */
export interface AdvanceLine {
  provider: string
  title: string
  topped_up_usd: string
  topped_up_rub: string
  used_usd: string
  used_rub: string
  generations: number
  left_usd: string
  left_rub: string
  overdrawn: boolean
}

export interface Advances {
  items: AdvanceLine[]
  topped_up_usd: string
  used_usd: string
  left_usd: string
  left_rub: string
}

/** GET /accounting/summary → `receivables`: users' debt (⚡ spent, payment refunded). */
export interface Receivables {
  debt_credits: string
  debtors: number
  credit_price_rub: string
  debt_rub_estimate: string
}

export interface AdvancesCardProps {
  advances?: Advances
  receivables?: Receivables
  error?: unknown
  onRetry?: () => void
}

function Row({ k, v, strong }: { k: string; v: string; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <span className={strong ? 'min-w-0 text-ink-1' : 'min-w-0 text-ink-2'}>{k}</span>
      <span className="tnum shrink-0 text-right text-ink-1">{v}</span>
    </div>
  )
}

/**
 * «Активы»: what the business has prepaid and is owed, as of now (docs/accounting.md «Аванс
 * провайдерам», «Долги пользователей»). A provider top-up is an advance: it shrinks as
 * generations spend it — the per-provider line reconciles «расход по генерациям» with
 * «пополнения». A negative rest is said in words (rule 21). Users' debt is a memo line, valued
 * at the ⚡ price: it is collected only if they pay again, so it never enters profit.
 */
export function AdvancesCard({ advances, receivables, error, onRetry }: AdvancesCardProps) {
  const header = (
    <SectionHeader
      as="h3"
      title="Авансы провайдерам и долги"
      description="На текущий момент. Пополнение провайдера — аванс: в прибыль идёт только расход по генерациям."
    />
  )
  if (advances === undefined) {
    return (
      <section>
        {header}
        <Card>
          {error ? (
            <ErrorState title="Не удалось загрузить авансы" error={error} onRetry={onRetry} />
          ) : (
            <div className="flex flex-col gap-2" role="status" aria-label="Загрузка">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-2/3" />
            </div>
          )}
        </Card>
      </section>
    )
  }
  return (
    <section>
      {header}
      <Card>
        {advances.items.length === 0 ? (
          <p className="text-body text-ink-2">Пополнений провайдеров и генераций пока нет.</p>
        ) : (
          <div className="flex flex-col gap-3 text-body">
            {advances.items.map((a) => (
              <div key={a.provider} className="flex flex-col gap-1 border-b border-hairline pb-3 last:border-b-0 last:pb-0">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-ink-1">{a.title}</span>
                  {a.overdrawn && (
                    <StatusBadge tone="warn" icon={<AlertTriangle />}>
                      Расход больше пополнений
                    </StatusBadge>
                  )}
                </div>
                <Row k="Пополнено" v={fmtMoney(a.topped_up_usd, 'USD')} />
                <Row
                  k={`Расход по генерациям (${fmtInt(a.generations)})`}
                  v={`${fmtMoney(a.used_usd, 'USD')}, ${fmtMoney(a.used_rub, 'RUB')}`}
                />
                <Row k="Остаток аванса" v={`${fmtMoney(a.left_usd, 'USD')}, ${fmtMoney(a.left_rub, 'RUB')}`} strong />
              </div>
            ))}
            <div className="border-t border-hairline pt-2">
              <Row k="Авансов всего" v={`${fmtMoney(advances.left_usd, 'USD')}, ${fmtMoney(advances.left_rub, 'RUB')}`} strong />
            </div>
          </div>
        )}
        {receivables && (
          <div className="mt-3 flex flex-col gap-1 border-t border-hairline pt-3 text-body">
            <Row
              k={`Долги пользователей (${fmtInt(receivables.debtors)})`}
              v={fmtCredits(receivables.debt_credits)}
            />
            <Row k={`Оценка по цене ${BOLT}`} v={fmtMoney(receivables.debt_rub_estimate, 'RUB')} />
            <p className="text-caption text-ink-3">
              {`${BOLT} потрачены, а оплата возвращена. Гасится из следующей оплаты; в прибыль не входит.`}
            </p>
          </div>
        )}
      </Card>
    </section>
  )
}
